import { useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';

import type { FleetMemberRow } from 'src/api/endpoints/cloudEnterprise.types';
import { Tooltip } from 'src/components';
import { Ellipsis } from 'src/components/Ellipsis';
import { ensureUniqueKeys } from 'src/utils/ensureUniqueKeys';

import { DeviceName } from 'src/components/DeviceName';
import { EnterpriseDeviceIcon } from './EnterpriseDeviceIcon';
import { fleetMemberRowHelpers } from './fleetMemberRowHelpers';

export function CellDevices({ row }: { row: FleetMemberRow }) {
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const addDeviceToSearch = (device: string) => {
    return navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        devices: [...(prev.devices ?? []), device],
        member: row.data.email,
      }),
    });
  };

  const removeDeviceFromSearch = (device: string) => {
    return navigate({
      to: '.',
      search: (prev) => ({
        ...search,
        devices: prev.devices?.filter((d) => d !== device),
      }),
    });
  };

  return (
    <div className="flex gap-4">
      <Ellipsis className="flex items-center gap-4">
        {ensureUniqueKeys(
          fleetMemberRowHelpers.getDevices(row),
          (d) => d.serialNumber
        ).map((device) => (
          <div key={device.key}>
            <Tooltip
              title={
                <span>
                  <DeviceName device={device} />{' '}
                  {fleetMemberRowHelpers.isDeviceNonCompliant(device) && (
                    <span>
                      <div className="mx-8 inline-block size-8 rounded-full bg-feedback-red-500 align-middle" />
                      Not compliant
                    </span>
                  )}
                </span>
              }
              asChild
            >
              <button
                className={clsx(
                  'shrink-0 rounded-[5px] transition-all hover:bg-paper-6'
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  void addDeviceToSearch(device.serialNumber ?? '');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();

                    if (search.devices?.includes(device.serialNumber ?? '')) {
                      void removeDeviceFromSearch(device.serialNumber ?? '');
                    } else {
                      void addDeviceToSearch(device.serialNumber ?? '');
                    }
                  }
                }}
              >
                <EnterpriseDeviceIcon
                  device={device}
                  active={fleetMemberRowHelpers.shouldHighlightDevice(
                    search.filter,
                    search.devices,
                    device
                  )}
                />
              </button>
            </Tooltip>
          </div>
        ))}
      </Ellipsis>
    </div>
  );
}
